import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IndianRupee, History, Plus } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EMPTY_REVISION = { effective_date: "", gross: "", structure_type: "Standard", basic: "", hra: "", travelling: "", tds_percent: "", ctc: "", reason: "" };

export default function AdminEmployeeSalary({ employee, onChange }) {
  const eid = employee.id;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_REVISION);

  const { data, isLoading } = useQuery({
    queryKey: ["salaryStructure", eid],
    queryFn: async () => (await api.get(`/employees/${eid}/salary-structure`)).data,
  });

  const revise = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/salary-structure`, {
      effective_date: form.effective_date,
      gross: Number(form.gross),
      structure_type: form.structure_type,
      basic: form.structure_type === "Custom" ? Number(form.basic) : undefined,
      hra: form.structure_type === "Custom" ? Number(form.hra) : undefined,
      travelling: form.structure_type === "Custom" ? Number(form.travelling) : undefined,
      tds_percent: form.structure_type === "Consolidated" ? Number(form.tds_percent || 0) : undefined,
      ctc: form.ctc ? Number(form.ctc) : undefined,
      reason: form.reason,
    })).data,
    onSuccess: () => {
      toast.success("Salary revision saved — payroll will use this from its effective date forward");
      qc.invalidateQueries({ queryKey: ["salaryStructure", eid] });
      setOpen(false); setForm(EMPTY_REVISION); onChange?.();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const current = data?.current;
  const history = data?.history || [];

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 shadow-none p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline text-slate-400 mb-1">Current Salary Structure</p>
            {isLoading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : current ? (
              current.structure_type === "Consolidated" ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 mt-2">
                  <div><span className="text-xs text-slate-400 block">Consolidated Fee / month</span><span className="text-navy font-semibold">{inr(current.gross)}</span></div>
                  <div><span className="text-xs text-slate-400 block">TDS</span><span className="text-navy">{current.tds_percent || 0}%</span></div>
                  <div><span className="text-xs text-slate-400 block">Structure</span><span className="text-navy">Consolidated (PF/ESI/Gratuity N/A)</span></div>
                  <div><span className="text-xs text-slate-400 block">Effective From</span><span className="text-navy">{current.effective_date}</span></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 mt-2">
                  <div><span className="text-xs text-slate-400 block">Gross / month</span><span className="text-navy font-semibold">{inr(current.gross)}</span></div>
                  <div><span className="text-xs text-slate-400 block">Basic (50%)</span><span className="text-navy">{inr(current.basic)}</span></div>
                  <div><span className="text-xs text-slate-400 block">HRA (40%)</span><span className="text-navy">{inr(current.hra)}</span></div>
                  <div><span className="text-xs text-slate-400 block">Travelling (10%)</span><span className="text-navy">{inr(current.travelling)}</span></div>
                  <div><span className="text-xs text-slate-400 block">CTC / month</span><span className="text-navy">{inr(current.ctc)}</span></div>
                  <div><span className="text-xs text-slate-400 block">Structure</span><span className="text-navy">{current.structure_type}</span></div>
                  <div><span className="text-xs text-slate-400 block">Effective From</span><span className="text-navy">{current.effective_date}</span></div>
                </div>
              )
            ) : (
              <p className="text-sm text-slate-400 mt-1">No formal salary revision on file yet — payroll will fall back to this employee's basic/gross fields until one is set.</p>
            )}
          </div>
          <Button size="sm" className="bg-navy hover:bg-navy-700" onClick={() => {
            setForm({ ...EMPTY_REVISION, gross: current?.gross || "", effective_date: new Date().toISOString().slice(0, 10) });
            setOpen(true);
          }} data-testid="revise-salary-btn">
            <IndianRupee className="h-4 w-4 mr-1" /> Revise Salary
          </Button>
        </div>
      </Card>

      {history.length > 0 && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-slate-500"><History className="h-4 w-4" /><span className="overline">Revision History</span></div>
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Effective</TableHead>
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="overline text-slate-500">Gross</TableHead>
              <TableHead className="overline text-slate-500">Basic</TableHead>
              <TableHead className="overline text-slate-500">HRA</TableHead>
              <TableHead className="overline text-slate-500">Travelling</TableHead>
              <TableHead className="overline text-slate-500">TDS %</TableHead>
              <TableHead className="overline text-slate-500">CTC</TableHead>
              <TableHead className="overline text-slate-500">Reason</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} className="table-row-i">
                  <TableCell className="text-slate-600">{h.effective_date}</TableCell>
                  <TableCell className="text-slate-600">{h.structure_type}</TableCell>
                  <TableCell className="text-navy font-medium">{inr(h.gross)}</TableCell>
                  {h.structure_type === "Consolidated" ? (
                    <TableCell colSpan={3} className="text-slate-400 text-xs italic">Consolidated fee — no Basic/HRA/Travelling split</TableCell>
                  ) : (
                    <>
                      <TableCell className="text-slate-600">{inr(h.basic)}</TableCell>
                      <TableCell className="text-slate-600">{inr(h.hra)}</TableCell>
                      <TableCell className="text-slate-600">{inr(h.travelling)}</TableCell>
                    </>
                  )}
                  <TableCell className="text-slate-600">{h.structure_type === "Consolidated" ? `${h.tds_percent || 0}%` : "—"}</TableCell>
                  <TableCell className="text-slate-600">{inr(h.ctc)}</TableCell>
                  <TableCell className="text-slate-500 min-w-[200px] max-w-[340px] whitespace-normal break-words" title={h.reason}>{h.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="revise-salary-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Revise Salary</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 -mt-2">This never overwrites a prior revision — it adds a new effective-dated record. Payroll for any month uses whichever revision was in force at that month's end.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Effective From</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="mt-1" /></div>
            <div><Label>{form.structure_type === "Consolidated" ? "Consolidated Fee / month" : "Gross Salary / month"}</Label><Input type="number" min="1" value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} className="mt-1" /></div>
          </div>
          <div>
            <Label>Salary Type</Label>
            <Select value={form.structure_type} onValueChange={(v) => setForm({ ...form, structure_type: v })}>
              <SelectTrigger className="mt-1" data-testid="structure-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Standard">Standard (50% Basic / 40% HRA / 10% Travelling)</SelectItem>
                <SelectItem value="Custom">Custom Basic / HRA / Travelling split</SelectItem>
                <SelectItem value="Consolidated">Consolidated fixed fee (Consultant / Trainee / Contract)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.structure_type === "Custom" && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Basic</Label><Input type="number" min="0" value={form.basic} onChange={(e) => setForm({ ...form, basic: e.target.value })} className="mt-1" /></div>
              <div><Label>HRA</Label><Input type="number" min="0" value={form.hra} onChange={(e) => setForm({ ...form, hra: e.target.value })} className="mt-1" /></div>
              <div><Label>Travelling</Label><Input type="number" min="0" value={form.travelling} onChange={(e) => setForm({ ...form, travelling: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          {form.structure_type === "Consolidated" && (
            <div className="space-y-2">
              <div><Label>TDS % (deducted automatically each month, e.g. 10 for 10%)</Label><Input type="number" min="0" max="100" step="0.01" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} className="mt-1" data-testid="tds-percent-input" /></div>
              <p className="text-xs text-slate-400">PF, ESI and Gratuity never apply to a Consolidated salary. The fee is paid in full every month regardless of attendance — apply a manual adjustment on the Payroll screen for a specific month if needed. PAN on file is required once TDS is deducted (Sec 206AA).</p>
            </div>
          )}
          <div><Label>CTC / month (optional — defaults to {form.structure_type === "Consolidated" ? "the fee" : "Gross"})</Label><Input type="number" min="0" value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} className="mt-1" /></div>
          <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" placeholder="e.g. Annual increment, promotion, market correction, new consultant engagement" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700"
              disabled={revise.isPending || !form.effective_date || !form.gross || form.reason.trim().length < 3 ||
                (form.structure_type === "Custom" && (!form.basic || !form.hra || !form.travelling))}
              onClick={() => revise.mutate()} data-testid="save-salary-revision">
              <Plus className="h-4 w-4 mr-1" /> Save Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
